import React, {useCallback, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';

import useOnResize from 'lib/hooks/useOnResize';

import AnnotationNoteIndicator from './AnnotationNoteIndicator';
import {
  resolveIndicatorScroller,
  scrollerContentOffset,
  selfAndAncestorsThrough
} from './annotationIndicatorPlacement';
import type {Annotation} from './annotationTypes';

const NO_TARGETS: Element[] = [];

interface AnnotationSupplementNoteIndicatorProps {
  /**
   * The annotatable supplement's element: the indicator lines up with its top and
   * portals into the pane that scrolls it. Resolved from here rather than from the
   * card, because a type renderer can wrap its card in an overflow container of its
   * own, which would capture the indicator inside the supplement.
   */
  region: HTMLElement | null;
  /** Every mark on the supplement carrying a note, in the order they read. */
  notedMarks: Annotation[];
}

interface Placement {
  scroller: HTMLElement;
  top: number;
}

/**
 * One margin button for everything noted on a supplement. Its marks share no line box
 * to sit beside, so it hangs from the supplement's top.
 */
export default function AnnotationSupplementNoteIndicator({
  region,
  notedMarks
}: AnnotationSupplementNoteIndicatorProps) {
  const [placement, setPlacement] = useState<Placement | null>(null);

  const measure = useCallback(() => {
    const scroller = notedMarks.length > 0 && resolveIndicatorScroller(region);
    if (!region || !scroller) {
      setPlacement(null);
      return;
    }
    setPlacement({
      scroller,
      top: region.getBoundingClientRect().top + scrollerContentOffset(scroller)
    });
  }, [region, notedMarks]);

  // The supplement's top moves when anything between it and the pane changes height.
  const resizeTargets = useMemo(() => {
    const scroller = resolveIndicatorScroller(region);
    return region && scroller ? selfAndAncestorsThrough(region, scroller) : NO_TARGETS;
  }, [region]);

  useOnResize(resizeTargets, measure);

  if (!placement || notedMarks.length === 0) {
    return null;
  }
  return createPortal(
    <AnnotationNoteIndicator align='top' line={{center: placement.top, annotations: notedMarks}} />,
    placement.scroller
  );
}
